import { useRef } from "react";
import useTranslator from "../../hooks/useTranslator";
import styles from "./Occupants.module.css";
import {
  Button,
  Card,
  ChoiceGroup,
  Field,
  FileInput,
  Icon,
  TextField,
} from "../design";
import constants from "../../Utils/constants";
import { haveValue, validateDniNie } from "../../helper/common";

// Company login tenants: one person who will live in the property (name,
// DNI/NIE or passport, ID proof files). Used by the onboarding occupants step
// and the occupants side modal on the profile page. ID proofs are saved as
// Tenant_docs (type constants.OCCUPANT_ID_PROOF); their ids are kept on
// Tenant.occupants[].id_proof_docs.

export const emptyOccupant = () => ({
  name: "",
  document_type: "dni_nie",
  document_number: "",
  id_proof_docs: [],
  files: [],
});

// Saved occupant (Tenant.occupants[]) -> form state.
export const toOccupantForm = (occupant) => ({
  ...emptyOccupant(),
  _id: occupant?._id,
  name: occupant?.name || "",
  document_type: occupant?.document_type || "dni_nie",
  document_number: occupant?.document_number || "",
  id_proof_docs: (occupant?.id_proof_docs || []).map((doc) =>
    String(doc?._id || doc)
  ),
});

// Returns { name?, document_number?, files? } error messages.
export const validateOccupant = (occupant, _t) => {
  const errors = {};
  if (!haveValue(occupant.name?.trim())) {
    errors.name = _t(
      "occupantNameRequired",
      "Occupants step: error when an occupant's full name is empty"
    );
  }
  const documentNumber = occupant.document_number?.trim().toUpperCase();
  if (!haveValue(documentNumber)) {
    errors.document_number = _t(
      "occupantDocumentNumberRequired",
      "Occupants step: error when an occupant's DNI/NIE or passport number is empty"
    );
  } else if (occupant.document_type === "dni_nie") {
    const dniNieType = /^[XYZ]/.test(documentNumber) ? "nie" : "dni";
    const hasValidFormat =
      dniNieType === "nie"
        ? /^[XYZ][0-9]{7}[A-Z]$/.test(documentNumber)
        : /^[0-9]{8}[A-Z]$/.test(documentNumber);
    if (!hasValidFormat || !validateDniNie(documentNumber, dniNieType)) {
      errors.document_number = _t(
        `invalid${dniNieType}`,
        "Invalid DNI/NIE number (checksum mismatch)"
      );
    }
  }
  if (!occupant.files.length && !occupant.id_proof_docs.length) {
    errors.files = _t(
      "occupantIdProofRequired",
      "Occupants step: error when no proof of ID file was added for an occupant"
    );
  }
  return errors;
};

// True when nothing was entered for this occupant (treated as skipped).
export const isEmptyOccupant = (occupant) =>
  !occupant.name?.trim() &&
  !occupant.document_number?.trim() &&
  !occupant.files.length &&
  !occupant.id_proof_docs.length;

// Uploads each occupant's new files and returns the Tenant.occupants payload.
export const buildOccupantsPayload = async (occupants, uploadFileData) => {
  const payload = [];
  for (const occupant of occupants) {
    let docIds = [];
    if (occupant.files.length) {
      const formData = new FormData();
      occupant.files.forEach(({ file }) => formData.append("tenantDocs", file));
      formData.append("fileType", constants.OCCUPANT_ID_PROOF);
      const res = await uploadFileData(formData);
      docIds = res?.data?.success_res?.docIds || [];
    }
    payload.push({
      ...(occupant._id && { _id: occupant._id }),
      name: occupant.name.trim(),
      document_type: occupant.document_type,
      document_number: occupant.document_number.trim().toUpperCase(),
      id_proof_docs: [...occupant.id_proof_docs, ...docIds],
    });
  }
  return payload;
};

// existingDocs: Tenant_docs by id, to show saved files by name (optional).
const OccupantForm = ({
  occupant,
  index,
  errors = {},
  onChange,
  heading,
  headerAction,
  existingDocs = {},
  // Onboarding step: only one occupant card is open; a collapsed card shows
  // a one-line summary and opens with the header (onToggle).
  collapsed = false,
  onToggle,
}) => {
  const { _t } = useTranslator();
  const fileInputRef = useRef(null);
  const removeLabel = _t(
    "removeFile",
    "Occupants step: link under a file preview to remove that file before saving"
  );

  const addFiles = (fileList) => {
    const added = Array.from(fileList).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    onChange({ files: [...occupant.files, ...added] });
  };

  const removeFile = (fileIndex) => {
    URL.revokeObjectURL(occupant.files[fileIndex].preview);
    onChange({ files: occupant.files.filter((_, i) => i !== fileIndex) });
  };

  const removeSavedDoc = (docId) => {
    onChange({
      id_proof_docs: occupant.id_proof_docs.filter((id) => id !== docId),
    });
  };

  // One field per line: name, document type, number, files.
  const fields = (
    <div className={styles.fields}>
      <div>
        <TextField
          id={`occupant_name_${index}`}
          label={_t(
            "occupantFullName",
            "Occupants step: label of the occupant's full name field"
          )}
          value={occupant.name}
          error={errors.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      <ChoiceGroup
        name={`occupant_document_type_${index}`}
        legend={_t("documentType", "Document type")}
        value={occupant.document_type}
        onChange={(e) => onChange({ document_type: e.target.value })}
        options={[
          { value: "dni_nie", label: _t("dniNie", "DNI / NIE") },
          { value: "passport", label: _t("passport", "Passport") },
        ]}
      />

      <TextField
        id={`occupant_document_number_${index}`}
        label={
          occupant.document_type === "passport"
            ? _t("passportNumber", "Passport number")
            : _t("dniNie", "DNI / NIE")
        }
        value={occupant.document_number}
        error={errors.document_number}
        onChange={(e) => onChange({ document_number: e.target.value })}
      />

      <div>
        <Field
          label={_t(
            "occupantDocuments",
            "Occupants: label of the documents of one occupant (ID proof or other documents)"
          )}
          error={errors.files}
        >
          <FileInput
            ref={fileInputRef}
            hidden
            multiple
            accept="application/pdf,image/jpeg,image/png"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <Button
            variant="secondary"
            size="sm"
            className={styles.addFiles}
            iconStart={<Icon name="upload" size={16} />}
            onClick={() => fileInputRef.current?.click()}
          >
            {_t(
              "addFiles",
              "Occupants form: button that opens the file picker to add proof of ID files"
            )}
          </Button>
          {occupant.id_proof_docs.map((docId) => (
            <div key={docId} className={styles.savedFile}>
              <span className={styles.fileName}>
                <Icon name="fileText" size={16} />
                {existingDocs[docId]?.file_name ||
                  _t(
                    "occupantSavedFile",
                    "Occupants form: placeholder name of a proof of ID file saved earlier"
                  )}
              </span>
              <span className={styles.fileActions}>
                {existingDocs[docId]?.url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={_t(
                      "viewDocument",
                      "Occupant view popup: button that opens one of the occupant's documents in a new tab"
                    )}
                    iconStart={<Icon name="eye" size={16} />}
                    onClick={() =>
                      window.open(
                        existingDocs[docId].url,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  />
                )}
                <Button
                  variant="dangerGhost"
                  size="sm"
                  iconOnly
                  aria-label={removeLabel}
                  iconStart={<Icon name="trash" size={16} />}
                  onClick={() => removeSavedDoc(docId)}
                />
              </span>
            </div>
          ))}
          {occupant.files.map(({ file, preview }, fileIndex) => (
            <div key={preview} className={styles.savedFile}>
              <span className={styles.fileName}>
                {file.type.startsWith("image/") ? (
                  <img src={preview} alt="" className={styles.previewThumb} />
                ) : (
                  <Icon name="fileText" size={16} />
                )}
                {file.name}
              </span>
              <span className={styles.fileActions}>
                <Button
                  variant="dangerGhost"
                  size="sm"
                  iconOnly
                  aria-label={removeLabel}
                  iconStart={<Icon name="trash" size={16} />}
                  onClick={() => removeFile(fileIndex)}
                />
              </span>
            </div>
          ))}
        </Field>
      </div>
    </div>
  );

  const fileCount =
    (occupant.files?.length ?? 0) + (occupant.id_proof_docs?.length ?? 0);
  const summary = [
    occupant.name,
    occupant.document_number
      ? `${
          occupant.document_type === "passport"
            ? _t("passport", "Passport")
            : _t("dniNie", "DNI / NIE")
        } ${occupant.document_number}`
      : null,
    fileCount
      ? `${fileCount} ${_t(
          "occupantFilesCount",
          "Occupants side modal: label after the number of documents of an occupant"
        )}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  // In the onboarding step each occupant is its own card (title + remove);
  // the side modal shows the fields alone.
  return heading || headerAction ? (
    <Card
      as="div"
      id={`occupant_card_${index}`}
      title={
        onToggle ? (
          <button
            type="button"
            className={styles.cardToggle}
            aria-expanded={!collapsed}
            onClick={onToggle}
          >
            <Icon name={collapsed ? "chevronRight" : "chevronDown"} size={18} />
            {heading}
          </button>
        ) : (
          heading
        )
      }
      description={collapsed && summary ? summary : undefined}
      actions={headerAction}
    >
      {!collapsed && fields}
    </Card>
  ) : (
    fields
  );
};

export default OccupantForm;
